import React, { useState, useMemo } from 'react';
import { useGetRentsByYearQuery } from '../../slices/rentsApiSlice';
import { Table, Form, Row, Col, Spinner, Alert, Button } from 'react-bootstrap';
import * as XLSX from 'xlsx';

const RentProratedScreen = () => {
  const [year, setYear] = useState('');
  const [submittedYear, setSubmittedYear] = useState(null);
  const [sortBy, setSortBy] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [landlordFilter, setLandlordFilter] = useState('');

  const {
    data: proratedRents,
    isLoading,
    error,
  } = useGetRentsByYearQuery(submittedYear, {
    skip: !submittedYear,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (year) {
      setSubmittedYear(year);
    }
  };

  const handleExport = () => {
    if (!Array.isArray(filteredRents)) return;

    const worksheetData = filteredRents.map((r) => ({
      Tenant: r.tenant?.name || '',
      House: r.house?.name || '',
      Landlord: r.landlord?.name || '',
      'Start Date': new Date(r.startDate).toLocaleDateString(),
      'End Date': new Date(r.endDate).toLocaleDateString(),
      'Total Rent': r.totalRent,
      [`Days in ${submittedYear}`]: r.daysInYear,
      'Prorated Rent': r.proratedRent,
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `Rents_${submittedYear}`);
    XLSX.writeFile(workbook, `Prorated_Rents_${submittedYear}.xlsx`);
  };

  const sortedRents = useMemo(() => {
    if (!Array.isArray(proratedRents)) return [];

    const sorted = [...proratedRents];
    if (sortBy === 'tenant') {
      sorted.sort((a, b) => (a.tenant?.name || '').localeCompare(b.tenant?.name || ''));
    } else if (sortBy === 'landlord') {
      sorted.sort((a, b) => (a.landlord?.name || '').localeCompare(b.landlord?.name || ''));
    }
    return sorted;
  }, [proratedRents, sortBy]);

  const filteredRents = useMemo(() => {
    return sortedRents.filter((r) => {
      const tenantMatch = r.tenant?.name?.toLowerCase().includes(tenantFilter.toLowerCase());
      const landlordMatch = r.landlord?.name?.toLowerCase().includes(landlordFilter.toLowerCase());
      return tenantMatch && landlordMatch;
    });
  }, [sortedRents, tenantFilter, landlordFilter]);

  const totals = useMemo(() => {
    return filteredRents.reduce(
      (acc, r) => {
        acc.totalRent += r.totalRent || 0;
        acc.proratedRent += r.proratedRent || 0;
        return acc;
      },
      { totalRent: 0, proratedRent: 0 }
    );
  }, [filteredRents]);

  return (
    <>
      <h1 className="mb-4">Prorated Rents by Year</h1>

      <Form onSubmit={handleSubmit} className="mb-4">
        <Row className="align-items-end g-3">
          <Col md={3}>
            <Form.Label>Year</Form.Label>
            <Form.Control
              type="number"
              placeholder="e.g. 2025"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            />
          </Col>
          <Col md={3}>
            <Form.Label>Sort By</Form.Label>
            <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="">— None —</option>
              <option value="tenant">Tenant Name (A–Z)</option>
              <option value="landlord">Landlord Name (A–Z)</option>
            </Form.Select>
          </Col>
          <Col md="auto">
            <Button type="submit" className="mt-2">Filter</Button>
          </Col>
        </Row>
      </Form>

      {isLoading ? (
        <Spinner animation="border" />
      ) : error ? (
        <Alert variant="danger">{error?.data?.message || error.error}</Alert>
      ) : submittedYear && Array.isArray(proratedRents) && proratedRents.length === 0 ? (
        <Alert variant="info">No rent records found for {submittedYear}.</Alert>
      ) : Array.isArray(filteredRents) ? (
        <>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Filter by tenant name"
                value={tenantFilter}
                onChange={(e) => setTenantFilter(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Filter by landlord name"
                value={landlordFilter}
                onChange={(e) => setLandlordFilter(e.target.value)}
              />
            </Col>
            <Col md="auto">
              <Button variant="success" onClick={handleExport}>
                Export to Excel
              </Button>
            </Col>
          </Row>

          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>House</th>
                <th>Landlord</th>
                <th>Start</th>
                <th>End</th>
                <th>Total Rent</th>
                <th>Days in {submittedYear}</th>
                <th>Prorated Rent</th>
              </tr>
            </thead>
            <tbody>
              {filteredRents.map((r) => (
                <tr key={r.rentId}>
                  <td>{r.tenant?.name || '—'}</td>
                  <td>{r.house?.name || '—'}</td>
                  <td>{r.landlord?.name || '—'}</td>
                  <td>{new Date(r.startDate).toLocaleDateString()}</td>
                  <td>{new Date(r.endDate).toLocaleDateString()}</td>
                  <td>{r.totalRent?.toLocaleString()}</td>
                  <td>{r.daysInYear}</td>
                  <td>{r.proratedRent?.toLocaleString()}</td>
                </tr>
              ))}
              <tr className="fw-bold">
                <td colSpan={5}>Total</td>
                <td>{totals.totalRent.toLocaleString()}</td>
                <td></td>
                <td>{totals.proratedRent.toLocaleString()}</td>
              </tr>
            </tbody>
          </Table>
        </>
      ) : null}
    </>
  );
};

export default RentProratedScreen;